import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getCampaign } from '../../../../services/CampaignServices';

import { superscriptionItems } from '../../../NewCampaign/NewCampaign.enums';

import { Campaign, Communication } from 'types';

import { CampaignContext } from '../../Campaign.context';

const comunicationsImages: { [key: string]: string } = {
	'nom035-closing': 'closing',
	'nom035-introduction': 'introduction',
	'nom035-reminder': 'reminder',
	'nom035-sensitization': 'sensitization',
	'nom035-evaluation': 'evaluation',
};

const IMAGE_BKT = import.meta.env.VITE_BKT;

const IMG_BASE_URL = `https://application-assets-${IMAGE_BKT}.s3.amazonaws.com/campaign/communications/placeholder/`;

export const useCommunications = ({ onFetchCommunications }: any) => {
	const navigate = useNavigate();
	const { id } = useParams() as { id: string };

	const { reloadCommunications, handleSetReloadCommunication } =
		useContext(CampaignContext);

	const [campaign, setCampaign] = useState({} as Campaign);
	const [isLoading, setIsLoading] = useState(false);

	const [communication, setCommunication] = useState({} as Communication);
	const [showCommunicationModal, setShowCommunicationModal] = useState(false);

	const [communicationCreatedDates, setCommunicationCreatedDates] = useState<
		any[] | undefined
	>([]);
	const [communicationCreatedTypes, setCommunicationCreatedTypes] = useState<
		any[] | undefined
	>([]);

	const handleNewCommunication = () => {
		setCommunication({} as Communication);
		setShowCommunicationModal(true);
	};

	const handleEditCommunication = (communication: Communication) => {
		setCommunication(communication);
		setShowCommunicationModal(true);
	};

	const handleCloseCommunication = () => {
		setCommunication({} as Communication);
		setShowCommunicationModal(false);
	};

	const fetchCampaign = async () => {
		setIsLoading(true);
		try {
			const {
				data: { content },
			} = await getCampaign(id);

			onFetchCommunications(Boolean(content.communications.length));
			setCampaign(content);
			setIsLoading(false);
		} catch (error) {
			if (error.response.status === 403) {
				navigate('/campaigns');
			}
		}
	};

	const getImage = (communication: any) => {
		const hasMessageFile = Boolean(communication.messageFile);

		if (hasMessageFile) return communication.messageFile;

		const imageName = comunicationsImages[communication.communicationType.id];

		return communication.useCustomTemplate === false
			? `${IMG_BASE_URL}${imageName}.png`
			: null;
	};

	useEffect(() => {
		const communicationsMap = campaign?.communications
			?.map((communication: Communication) => {
				const type = communication.communicationType.id.replace('nom035-', '');
				const itemSection = superscriptionItems.find(
					(item: any) => item.type === type,
				);

				if (!itemSection) return null;

				return {
					type: type,
					date: {
						color: itemSection.color,
						date: communication.sendingDateTime.split('T')[0] + 'T00:00:00',
					},
				};
			})
			.filter((value) => value !== null);

		if (communicationsMap) {
			setCommunicationCreatedDates(
				communicationsMap?.map((item: any) => {
					return item.date;
				}),
			);

			setCommunicationCreatedTypes(
				communicationsMap?.map((item: any) => item?.type && item.type),
			);
		}
	}, [campaign]);

	useEffect(() => {
		if (reloadCommunications) {
			fetchCampaign();
			handleSetReloadCommunication();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reloadCommunications]);

	useEffect(() => {
		fetchCampaign();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		campaign,
		showCommunicationModal,
		communication,
		isLoading,
		communicationCreatedDates,
		communicationCreatedTypes,
		getImage,
		handleNewCommunication,
		handleEditCommunication,
		handleCloseCommunication,
	};
};
